"use client";

import { useEffect, useState } from "react";
import { FlaskConical } from "lucide-react";

const POC_ENABLED = import.meta.env.VITE_POC_MODE === "1";

const states = [
  { label: "Successful run", value: "success" },
  { label: "Running", value: "running" },
  { label: "Failed run", value: "failed" }
] as const;

export function PocBanner() {
  const [ready, setReady] = useState(false);
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => setReady(true), []);

  if (!POC_ENABLED) return null;

  async function selectState(value: (typeof states)[number]["value"]) {
    setPending(value);
    try {
      const response = await fetch(`/api/poc/collector-state?state=${value}`, {
        method: "POST"
      });
      if (!response.ok) throw new Error("POC state update failed");
      window.location.reload();
    } finally {
      setPending(null);
    }
  }

  return (
    <section
      aria-label="Local proof of concept controls"
      data-poc-banner
      className="border-b border-amber-400/25 bg-amber-300/[0.07] px-4 py-3 md:px-6"
    >
      <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <FlaskConical className="mt-0.5 shrink-0 text-amber-300" size={17} aria-hidden="true" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-amber-200">Local POC</p>
            <p className="mt-0.5 text-xs leading-5 text-[var(--hc-muted)]">
              Synthetic fixture data — validates the collector, API, state handling, charts, and responsive UI without a paid browser plan.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2" aria-label="Collector state simulation">
          {states.map((state) => (
            <button
              key={state.value}
              type="button"
              disabled={!ready || pending !== null}
              onClick={() => selectState(state.value)}
              className="min-h-10 rounded-md border border-[var(--hc-border-strong)] bg-[var(--hc-surface)] px-3 text-xs font-semibold text-[var(--hc-text)] transition hover:border-amber-300/60 hover:text-amber-200 disabled:cursor-wait disabled:opacity-50"
            >
              {pending === state.value ? "Applying…" : state.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
